import React, { useState, useEffect } from 'react';
import {
  Bell, AlertTriangle, Zap, Calendar, ChevronDown, ChevronRight, X, Plus
} from 'lucide-react';
import './DispatchAttentionBar.css';

const STORAGE_KEY = 'dispatch.attention.expanded';

const typeLabels = { pto: 'PTO', sick: 'Sick', personal: 'Personal', unpaid: 'Unpaid' };

const DispatchAttentionBar = React.forwardRef(function DispatchAttentionBar({
  unscheduledJobs,
  availableTeamMembers,
  showOpenSlots,
  setShowOpenSlots,
  unassignedJobsCount,
  timeOffToday,
  pendingTimeOff,
  getEmployeeById,
  onScheduleUnscheduled,
  onQuickAdd,
  onOpenTimeOff,
}, ref) {
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, expanded ? '1' : '0'); } catch (e) { /* noop */ }
  }, [expanded]);

  const showCapacity = showOpenSlots && availableTeamMembers.length > 0 && unassignedJobsCount === 0;
  const showUnscheduled = unscheduledJobs.length > 0;
  const showTimeOff = timeOffToday.length > 0;
  const showPendingTimeOff = pendingTimeOff.length > 0;

  if (!showUnscheduled && !showCapacity && !showTimeOff && !showPendingTimeOff) {
    return null;
  }

  const summary = [];
  if (showUnscheduled) summary.push({ icon: <Bell size={14} />, text: `${unscheduledJobs.length} to schedule`, tone: 'danger' });
  if (showCapacity) summary.push({ icon: <Zap size={14} />, text: `${availableTeamMembers.length} available`, tone: 'warning' });
  if (showTimeOff) summary.push({ icon: <Calendar size={14} />, text: `${timeOffToday.length} off today`, tone: 'info' });
  if (showPendingTimeOff) summary.push({ icon: <AlertTriangle size={14} />, text: `${pendingTimeOff.length} time-off pending`, tone: 'warning' });

  return (
    <div ref={ref} className={`attention-bar ${expanded ? 'expanded' : 'collapsed'}`}>
      <button
        type="button"
        className="attention-bar-summary"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <div className="attention-summary-items">
          {summary.map((s, i) => (
            <span key={i} className={`attention-summary-chip tone-${s.tone}`}>
              {s.icon}
              {s.text}
            </span>
          ))}
        </div>
        <span className="attention-bar-toggle">
          {expanded ? 'Hide' : 'Show details'}
          <ChevronDown size={14} className={expanded ? 'rotated' : ''} />
        </span>
      </button>

      {expanded && (
        <div className="attention-bar-body">
          {showUnscheduled && (
            <div className="unscheduled-alert">
              <div className="alert-content">
                <Bell size={20} />
                <div>
                  <strong>{unscheduledJobs.length} new client{unscheduledJobs.length > 1 ? 's' : ''} need{unscheduledJobs.length === 1 ? 's' : ''} to be scheduled</strong>
                  <span>Approved estimates waiting for first appointment</span>
                </div>
              </div>
              <div className="unscheduled-jobs-list">
                {unscheduledJobs.map(job => (
                  <div key={job.id} className="unscheduled-job-card">
                    <div className="unscheduled-job-info">
                      <span className="unscheduled-client">{job.clientName}</span>
                      <span className="unscheduled-address">{job.address}</span>
                      <span className="unscheduled-service">
                        {job.isDeepClean ? 'Deep Clean' : job.frequency} • ${job.price}
                      </span>
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => onScheduleUnscheduled(job)}
                    >
                      <Calendar size={14} /> Schedule
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showCapacity && (
            <div className="capacity-alert">
              <div className="capacity-content">
                <Zap size={20} />
                <div>
                  <strong>{availableTeamMembers.length} team member{availableTeamMembers.length > 1 ? 's' : ''} available:</strong>
                  <span>{availableTeamMembers.map(e => e.displayName).join(', ')}</span>
                </div>
              </div>
              <div className="capacity-actions">
                <button className="btn btn-warning" onClick={onQuickAdd}>
                  <Plus size={16} /> Add Job
                </button>
                <button className="btn-icon" onClick={() => setShowOpenSlots(false)} title="Dismiss">
                  <X size={18} />
                </button>
              </div>
            </div>
          )}

          {showTimeOff && (
            <div className="time-off-banner">
              <div className="time-off-banner-header">
                <Calendar size={16} />
                <strong>{timeOffToday.length} team member{timeOffToday.length !== 1 ? 's' : ''} off</strong>
              </div>
              <div className="time-off-banner-list">
                {timeOffToday.map(req => {
                  const emp = getEmployeeById(req.employeeId);
                  const empName = emp ? `${emp.firstName} ${emp.lastName?.[0]}.` : 'Unknown';
                  return (
                    <div key={req.id} className={`time-off-person ${req.status}`}>
                      <span className="time-off-name">{empName}</span>
                      <span className={`time-off-type-tag ${req.type}`}>{typeLabels[req.type] || req.type}</span>
                      {req.status === 'pending' && <span className="time-off-pending-tag">PENDING</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {showPendingTimeOff && (
            <div className="pending-timeoff-alert" onClick={onOpenTimeOff}>
              <Bell size={16} />
              <span>
                <strong>{pendingTimeOff.length}</strong> pending time off request{pendingTimeOff.length !== 1 ? 's' : ''} to review
              </span>
              <ChevronRight size={16} />
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default DispatchAttentionBar;
